import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const CarRentalBookings = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const response = await api.get('/car-rentals/bookings/me');
      setBookings(response.data.bookings || []);
    } catch (err) {
      setBookings([]);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'info',
      active: 'success',
      completed: 'default',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      active: 'En cours',
      completed: 'Terminée',
      cancelled: 'Annulée',
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <PendingIcon />,
      confirmed: <CheckCircleIcon />,
      active: <DirectionsCarIcon />,
      completed: <CheckCircleIcon />,
      cancelled: <CancelIcon />,
    };
    return icons[status];
  };

  const filteredBookings = bookings.filter(booking => {
    if (tabValue === 0) return ['confirmed', 'active'].includes(booking.status);
    if (tabValue === 1) return booking.status === 'completed';
    if (tabValue === 2) return booking.status === 'cancelled';
    return true;
  });

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Mes Réservations
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Gérez toutes vos locations de voitures
        </Typography>

        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
          <Tab label="En cours" />
          <Tab label="Terminées" />
          <Tab label="Annulées" />
        </Tabs>

        <Grid container spacing={3}>
          {filteredBookings.map((booking) => (
            <Grid item xs={12} key={booking.id}>
              <Card>
                <CardContent>
                  <Grid container spacing={3}>
                    {/* Car Image */}
                    <Grid item xs={12} sm={3}>
                      <Box
                        component="img"
                        src={booking.car.image}
                        alt={`${booking.car.brand} ${booking.car.model}`}
                        sx={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 2 }}
                      />
                    </Grid>

                    {/* Booking Details */}
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Typography variant="h6" fontWeight={700}>
                          {booking.car.brand} {booking.car.model}
                        </Typography>
                        <Chip
                          icon={getStatusIcon(booking.status)}
                          label={getStatusLabel(booking.status)}
                          color={getStatusColor(booking.status)}
                          size="small"
                        />
                      </Box>

                      <Box display="flex" flexDirection="column" gap={1.5}>
                        <Box display="flex" alignItems="start" gap={1}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Période de location
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {booking.pickupDate} → {booking.returnDate}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {booking.days} jours
                            </Typography>
                          </Box>
                        </Box>

                        <Box display="flex" alignItems="start" gap={1}>
                          <LocationOnIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Lieux
                            </Typography>
                            <Typography variant="body2">
                              Prise: {booking.pickupLocation}
                            </Typography>
                            <Typography variant="body2">
                              Retour: {booking.returnLocation}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Price & Actions */}
                    <Grid item xs={12} sm={3}>
                      <Box display="flex" flexDirection="column" height="100%" justifyContent="space-between">
                        <Box>
                          <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
                            {booking.totalPrice}€
                          </Typography>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={1} mt={2}>
                          {booking.status === 'confirmed' && (
                            <>
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                sx={{
                                  bgcolor: '#FFD700',
                                  color: '#000',
                                  fontWeight: 600,
                                  '&:hover': { bgcolor: '#FFC700' }
                                }}
                              >
                                Voir détails
                              </Button>
                              <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                color="error"
                              >
                                Annuler
                              </Button>
                            </>
                          )}
                          {booking.status === 'active' && (
                            <Button
                              fullWidth
                              variant="contained"
                              size="small"
                              sx={{
                                bgcolor: '#FFD700',
                                color: '#000',
                                fontWeight: 600,
                                '&:hover': { bgcolor: '#FFC700' }
                              }}
                            >
                              Contacter propriétaire
                            </Button>
                          )}
                          {booking.status === 'completed' && (
                            <Button
                              fullWidth
                              variant="outlined"
                              size="small"
                            >
                              Louer à nouveau
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredBookings.length === 0 && (
          <Box textAlign="center" py={8}>
            <DirectionsCarIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Aucune réservation
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/car-rental')}
              sx={{
                mt: 2,
                bgcolor: '#FFD700',
                color: '#000',
                fontWeight: 700,
                '&:hover': { bgcolor: '#FFC700' }
              }}
            >
              Parcourir les véhicules
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default CarRentalBookings;
